import { Box, Button, Divider, Modal, Stack } from "@mui/material";
import { ChangeEvent, useRef, useState } from "react";
import {
  FieldPath,
  FieldValues,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import { BaseFieldProps } from "./FieldProps";
import Image, { IMAGE_SIZE } from "./Image";
import Text from "./Text";
import useIsFieldDisabled from "../../hooks/formHooks/useIsFieldDisabled";
import useNotify from "../../hooks/useNotify";
import { useFormStore } from "../../stores/formStore";
import UploadIcon from "../icons/text-formatting/UploadIcon";

type ImageData = {
  filename: string;
  url: string;
  file: File;
};

type Props<TFieldValues extends FieldValues> = {
  imageSize: IMAGE_SIZE;
} & BaseFieldProps<TFieldValues>;

const ImageUploadField = <TFieldValues extends FieldValues>({
  name,
  label,
  imageSize,
  disabled = false,
  disabledForEdit = false,
}: Props<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();
  const { field } = useController({
    name,
    control,
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<ImageData>();
  const addImageFile = useFormStore(state => state.addImageFile);

  const { setNotifyError } = useNotify();

  const isFieldDisabled = useIsFieldDisabled(disabled, disabledForEdit);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const allowedExtensions = ["jpg", "png", "jpeg", "svg"];
      const file = event.target.files?.[0];

      if (!file) return;

      if (file.size > 10000000) throw new Error("File size exceeds 10 MB");

      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension))
        throw new Error("File type is not supported");

      setSelectedImage({
        filename: file.name,
        url: URL.createObjectURL(file),
        file,
      });

      setIsModalOpen(true);
    } catch (error) {
      const { message } = error as Error;
      setNotifyError(message);
      setSelectedImage(undefined);
    }
  };

  const onUploadImage = () => {
    if (selectedImage) {
      field.onChange(
        selectedImage.url as PathValue<TFieldValues, FieldPath<TFieldValues>>,
      );

      addImageFile(field.name, selectedImage.file);
    }

    setIsModalOpen(false);
  };

  const onCancelUpload = () => {
    setSelectedImage(undefined);
    setIsModalOpen(false);
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg, .jpeg, .png, .svg"
        onChange={handleImageChange}
        hidden
        data-testid={`form-${label}-IMAGE_UPLOAD`}
      />
      <Divider sx={{ marginY: 1 }} />
      <Stack
        direction="row"
        spacing="15px"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <Stack spacing={2}>
          <Text variant="body_large">{label}</Text>
          <Button
            variant="secondary"
            disabled={isFieldDisabled}
            size="small"
            sx={{ width: 120 }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Text variant="title_xsmall">Upload</Text>
              <UploadIcon width={16} height={16} />
            </Stack>
          </Button>
        </Stack>
        <Image imageUri={field.value} imageSize={imageSize} alt={label} />
      </Stack>

      <Modal
        open={isModalOpen}
        onClose={onCancelUpload}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box width={532} bgcolor="mono.white" borderRadius={2.5} pb={5}>
          <Box
            px={2.5}
            py={2}
            borderRadius={"10px 10px 0 0"}
            bgcolor="mono.light.grey"
          >
            <Text variant={"title_medium"}>Upload {label.toLowerCase()}</Text>
          </Box>
          <Stack p={3} alignItems="center">
            <Image
              scale={1.36}
              imageUri={selectedImage?.url}
              imageSize={imageSize}
              alt={label}
            />
            <Text my={2} variant={"body_large"}>
              {selectedImage?.filename}
            </Text>
            <Stack direction="row" spacing={1} width={256}>
              <Button
                variant="secondary"
                size="small"
                fullWidth={true}
                onClick={onCancelUpload}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                fullWidth={true}
                onClick={onUploadImage}
              >
                Upload
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default ImageUploadField;
